import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { ArrowLeft, Save, Plus, Trash2, Search, Upload, File, Image, X, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Card } from '../../components/ui/Card';
import { formatCurrency } from '../../lib/utils';
import { useCompra, useCreateCompra, useUpdateCompra } from '../../hooks/useCompras';
import { useSuppliers } from '../../hooks/useSuppliers';
import { useProducts } from '../../hooks/useProducts';
import { useDebounce } from '../../hooks/useDebounce';
import { z } from 'zod';
import api from '../../api/clients';

const compraDetalleSchema = z.object({
  productoId: z.number().int().positive('El producto es requerido'),
  cantidad: z.number().int().min(1, 'La cantidad debe ser al menos 1'),
  precioUnitario: z.number().min(0, 'El precio no puede ser negativo'),
  subtotal: z.number().min(0),
  observaciones: z.string().optional(),
});

const compraSchema = z.object({
  proveedorId: z.number().int().positive('El proveedor es requerido'),
  fechaEntrega: z.string().optional().nullable(),
  costoEnvio: z.number().min(0, 'El costo de envío no puede ser negativo').default(0),
  descuento: z.number().min(0).max(100).default(0),
  observaciones: z.string().optional(),
  detalles: z.array(compraDetalleSchema).min(1, 'Agrega al menos un producto'),
  total: z.number().min(0).default(0),
  imagenes: z.string().optional().nullable(),
  pdfUrl: z.string().optional().nullable(),
});

type CompraFormData = z.infer<typeof compraSchema>;

export function CompraForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = !!id;

  const { data: compra, isLoading: compraLoading } = useCompra(id ? parseInt(id) : 0);
  const { data: suppliersData } = useSuppliers({ limit: 1000 });

  const suppliers = suppliersData?.data ?? [];
  const createCompra = useCreateCompra();
  const updateCompra = useUpdateCompra();

  const [searchProduct, setSearchProduct] = useState('');
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  const [cantidad, setCantidad] = useState(1);
  const productDropdownRef = useRef<HTMLDivElement>(null);

  const [imagenes, setImagenes] = useState<string[]>([]);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const debouncedSearch = useDebounce(searchProduct, 300);

  const shouldSearchProducts =
    searchProduct.trim().length > 0 &&
    (!selectedProduct || selectedProduct.nombre !== searchProduct);

  const { data: productsData, isLoading: loadingProducts } = useProducts({
    search: shouldSearchProducts ? debouncedSearch.trim() : undefined,
    limit: 20,
    activo: true,
  });

  const filteredProducts = productsData?.data ?? [];

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<CompraFormData>({
    resolver: zodResolver(compraSchema),
    defaultValues: {
      proveedorId: undefined,
      fechaEntrega: '',
      costoEnvio: 0,
      descuento: 0,
      observaciones: '',
      detalles: [],
      imagenes: null,
      pdfUrl: null,
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'detalles',
  });

  const watchDetalles = watch('detalles');
  const watchCostoEnvio = watch('costoEnvio');
  const watchDescuento = watch('descuento');

  const subtotal = watchDetalles?.reduce((sum, d) => sum + (d.subtotal || 0), 0) || 0;
  const descuentoMonto = subtotal * ((watchDescuento || 0) / 100);
  const total = subtotal + (watchCostoEnvio || 0) - descuentoMonto;

  const uploadFiles = async (files: File[]): Promise<{ imagenes: string[]; pdfUrl: string | null }> => {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));

    const response = await api.post('/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (progressEvent) => {
        if (progressEvent.total) {
          setUploadProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total));
        }
      },
    });

    return response.data.data;
  };

  useEffect(() => {
    if (compra && isEditing) {
      setValue('proveedorId', compra.proveedorId);
      setValue('fechaEntrega', compra.fechaEntrega ? compra.fechaEntrega.split('T')[0] : '');
      setValue('costoEnvio', Number(compra.costoEnvio || 0));
      setValue('descuento', Number(compra.descuento || 0));
      setValue('observaciones', compra.observaciones || '');

      if (compra.detalles && compra.detalles.length > 0) {
        setValue('detalles', compra.detalles.map((d: any) => ({
          productoId: d.productoId,
          cantidad: d.cantidad,
          precioUnitario: d.precioUnitario,
          subtotal: d.subtotal,
          observaciones: d.observaciones || '',
        })));
      }

      if (compra.imagenes) {
        try {
          const urls = typeof compra.imagenes === 'string'
            ? compra.imagenes.split(',').filter((u: string) => u.trim())
            : [];
          setImagenes(urls);
        } catch {
          setImagenes([]);
        }
      }
      if (compra.pdfUrl) {
        setPdfUrl(compra.pdfUrl);
      }
    }
  }, [compra, isEditing, setValue]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (productDropdownRef.current && !productDropdownRef.current.contains(event.target as Node)) {
        setShowProductDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleAddProduct = () => {
    if (!selectedProduct) {
      toast.error('Selecciona un producto primero');
      return;
    }

    if (cantidad <= 0) {
      toast.error('La cantidad debe ser mayor a 0');
      return;
    }

    const precio = Number(selectedProduct.precioCosto) || 0;
    const subtotal = cantidad * precio;

    const exists = watchDetalles?.some(d => d.productoId === selectedProduct.id);
    if (exists) {
      toast.error('El producto ya está agregado');
      return;
    }

    append({
      productoId: selectedProduct.id,
      cantidad,
      precioUnitario: precio,
      subtotal,
      observaciones: '',
    });

    setSelectedProduct(null);
    setSearchProduct('');
    setCantidad(1);
    setShowProductDropdown(false);
    toast.success('Producto agregado');
  };

  const handleClearProduct = () => {
    setSelectedProduct(null);
    setSearchProduct('');
    setCantidad(1);
    setShowProductDropdown(false);
  };

  const handleRemoveProduct = (index: number) => {
    remove(index);
  };

  const handleCantidadChange = (index: number, newCantidad: number) => {
    if (newCantidad < 1) return;
    const current = watchDetalles[index];
    if (current) {
      const newSubtotal = newCantidad * current.precioUnitario;
      setValue(`detalles.${index}.cantidad`, newCantidad);
      setValue(`detalles.${index}.subtotal`, newSubtotal);
    }
  };

  const handlePrecioChange = (index: number, newPrecio: number) => {
    if (newPrecio < 0) return;
    const current = watchDetalles[index];
    if (current) {
      const newSubtotal = current.cantidad * newPrecio;
      setValue(`detalles.${index}.precioUnitario`, newPrecio);
      setValue(`detalles.${index}.subtotal`, newSubtotal);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const totalFiles = selectedFiles.length + files.length;

    if (totalFiles > 3) {
      toast.error('Máximo 3 archivos (2 imágenes + 1 PDF)');
      return;
    }

    const imagenesCount = selectedFiles.filter(f => f.type.startsWith('image/')).length +
      files.filter(f => f.type.startsWith('image/')).length;
    const pdfCount = selectedFiles.filter(f => f.type === 'application/pdf').length +
      files.filter(f => f.type === 'application/pdf').length;

    if (imagenesCount > 2) {
      toast.error('Máximo 2 imágenes');
      return;
    }
    if (pdfCount > 1) {
      toast.error('Máximo 1 PDF');
      return;
    }

    const validFiles = files.filter(file => {
      const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
      if (!validTypes.includes(file.type)) {
        toast.error(`"${file.name}" no es un formato válido`);
        return false;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`"${file.name}" supera el límite de 10MB`);
        return false;
      }
      return true;
    });

    setSelectedFiles(prev => [...prev, ...validFiles]);
    e.target.value = '';
  };

  const removeSelectedFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const removeImagen = (url: string) => {
    setImagenes(prev => prev.filter(u => u !== url));
  };

  const removePdf = () => {
    setPdfUrl(null);
  };

  const onSubmit = async (data: CompraFormData) => {
    try {
      setUploading(true);
      setUploadProgress(0);

      let imagenesUrls: string[] = [...imagenes];
      let pdfUrlFinal: string | null = pdfUrl;

      if (selectedFiles.length > 0) {
        const result = await uploadFiles(selectedFiles);
        imagenesUrls = [...imagenesUrls, ...result.imagenes];
        pdfUrlFinal = result.pdfUrl || pdfUrlFinal;
      }

      const subtotal = watchDetalles?.reduce((sum, d) => sum + (d.subtotal || 0), 0) || 0;
      const descuentoPorcentaje = data.descuento || 0;
      const total = subtotal + (data.costoEnvio || 0) - (subtotal * (descuentoPorcentaje / 100));

      const payload = {
        proveedorId: Number(data.proveedorId),
        fechaEntrega: data.fechaEntrega || null,
        costoEnvio: Number(data.costoEnvio || 0),
        descuento: descuentoPorcentaje,
        observaciones: data.observaciones || null,
        detalles: data.detalles.map(d => ({
          productoId: Number(d.productoId),
          cantidad: Number(d.cantidad),
          precioUnitario: Number(d.precioUnitario),
          subtotal: Number(d.subtotal) || (Number(d.cantidad) * Number(d.precioUnitario)),
          observaciones: d.observaciones || null,
        })),
        total,
        imagenes: imagenesUrls.join(','),
        pdfUrl: pdfUrlFinal,
      };

      if (isEditing && id) {
        await updateCompra.mutateAsync({ id: parseInt(id), data: payload });
      } else {
        await createCompra.mutateAsync(payload);
      }
      navigate('/compras');
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Error al guardar la compra');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const totalFiles = imagenes.length + (pdfUrl ? 1 : 0) + selectedFiles.length;

  if (compraLoading && isEditing) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Cargando compra...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button onClick={() => navigate('/compras')} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-2xl font-bold text-gray-800">
          {isEditing ? 'Editar Compra' : 'Nueva Compra'}
        </h1>
      </div>

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {/* Datos de la compra */}
            <Card className="p-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">Datos de la Compra</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Proveedor *</label>
                  <select
                    {...register('proveedorId', { valueAsNumber: true })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Seleccionar proveedor...</option>
                    {suppliers.map((sup: any) => (
                      <option key={sup.id} value={sup.id}>{sup.nombre}</option>
                    ))}
                  </select>
                  {errors.proveedorId && (
                    <p className="text-sm text-red-600 mt-1">{errors.proveedorId.message}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Fecha y Hora de Entrega Estimada</label>
                  <Input type="datetime-local" {...register('fechaEntrega')} error={errors.fechaEntrega?.message} />
                </div>
              </div>
            </Card>

            {/* Productos */}
            <Card className="p-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">Productos</h2>
              <div className="flex flex-wrap gap-3 mb-4">
                <div className="relative flex-1 min-w-50" ref={productDropdownRef}>
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  <Input
                    type="text"
                    placeholder="Buscar producto..."
                    value={searchProduct}
                    onChange={(e) => {
                      setSearchProduct(e.target.value);
                      setShowProductDropdown(true);
                      if (selectedProduct && e.target.value !== selectedProduct.nombre) {
                        setSelectedProduct(null);
                      }
                    }}
                    onFocus={() => setShowProductDropdown(true)}
                    className="pl-10 pr-10"
                  />

                  {selectedProduct && (
                    <button
                      type="button"
                      onClick={handleClearProduct}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      title="Quitar producto seleccionado"
                    >
                      <X size={16} />
                    </button>
                  )}

                  {showProductDropdown && searchProduct && filteredProducts.length > 0 && (
                    <div
                      className="absolute left-0 right-0 z-9999 mt-1 bg-white border border-gray-200 rounded-lg shadow-2xl"
                      style={{
                        maxHeight: '200px',
                        height: 'auto',
                        overflowY: 'auto',
                        WebkitOverflowScrolling: 'touch',
                        scrollBehavior: 'smooth'
                      }}
                    >
                      {filteredProducts.map((product: any) => {
                        const truncateDescription = (text: string) => {
                          if (!text) return '';
                          if (text.length <= 60) return text;
                          return text.substring(0, 60) + '.....';
                        };

                        return (
                          <button
                            key={product.id}
                            type="button"
                            className="w-full text-left px-4 py-2.5 hover:bg-blue-50 transition-colors flex justify-between items-center border-b border-gray-100 last:border-0"
                            onClick={() => {
                              setSelectedProduct(product);
                              setSearchProduct(product.nombre);
                              setShowProductDropdown(false);
                            }}
                          >
                            <div className="flex-1 min-w-0 pr-3">
                              <p className="font-medium text-gray-800 truncate">{product.nombre}</p>
                              {product.descripcion && (
                                <p className="text-xs text-gray-400 truncate">
                                  {truncateDescription(product.descripcion)}
                                </p>
                              )}
                            </div>
                            <span className="text-sm font-semibold text-blue-600 shrink-0">
                              {formatCurrency(product.precioCosto || 0)}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {showProductDropdown && searchProduct && loadingProducts && (
                    <div className="absolute left-0 right-0 z-9999 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-4 text-center text-gray-500">
                      Buscando productos...
                    </div>
                  )}

                  {showProductDropdown && searchProduct && !loadingProducts && filteredProducts.length === 0 && (
                    <div className="absolute left-0 right-0 z-9999 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-4 text-center text-gray-500">
                      No se encontraron productos
                    </div>
                  )}
                </div>

                <div className="w-24">
                  <Input type="number" value={cantidad} onChange={(e) => setCantidad(Number(e.target.value) || 0)} min={1} />
                </div>

                <Button type="button" onClick={handleAddProduct} className="bg-blue-600 hover:bg-blue-700 text-white">
                  <Plus size={18} /> Agregar
                </Button>
              </div>

              <div className="space-y-2">
                {fields.map((field, index) => (
                  <div key={field.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <p className="font-medium text-gray-800">
                        {field.productoId
                          ? (productsData?.data as any[])?.find((p) => p.id === field.productoId)?.nombre
                            || (field as any).nombre
                            || `Producto #${field.productoId}`
                          : 'Producto'}
                      </p>
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <span>Cantidad: {field.cantidad}</span>
                        <span>|</span>
                        <span>Precio: {formatCurrency(field.precioUnitario)}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-20">
                        <Input type="number" value={field.cantidad} onChange={(e) => handleCantidadChange(index, Number(e.target.value) || 0)} min={1} className="text-center" />
                      </div>
                      <div className="w-24">
                        <Input type="number" step="0.01" value={field.precioUnitario} onChange={(e) => handlePrecioChange(index, Number(e.target.value) || 0)} min={0} className="text-center" />
                      </div>
                      <p className="font-semibold text-gray-800 min-w-20 text-right">{formatCurrency(field.subtotal)}</p>
                      <button type="button" onClick={() => handleRemoveProduct(index)} className="p-1 text-red-500 hover:bg-red-50 rounded">
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                ))}
                {fields.length === 0 && (
                  <p className="text-center text-gray-400 py-4">No hay productos agregados</p>
                )}
              </div>
            </Card>

            {/* Archivos */}
            <Card className="p-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">Archivos</h2>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Upload size={16} className="inline mr-1" />
                  Subir archivos (2 imágenes + 1 PDF, máx. 10MB cada uno)
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="file"
                    accept="image/*,application/pdf"
                    multiple
                    onChange={handleFileSelect}
                    className="hidden"
                    id="file-upload"
                    disabled={totalFiles >= 3}
                  />
                  <label
                    htmlFor="file-upload"
                    className={`px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors cursor-pointer flex items-center gap-2 ${
                      totalFiles >= 3 ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    <Upload size={18} className="text-gray-400" />
                    <span className="text-sm text-gray-500">
                      {totalFiles >= 3 ? 'Límite alcanzado' : 'Seleccionar archivos'}
                    </span>
                  </label>
                  <span className="text-xs text-gray-400">{totalFiles}/3 archivos</span>
                  {uploading && (
                    <div className="flex items-center gap-2">
                      <Loader2 size={16} className="animate-spin text-blue-600" />
                      <span className="text-xs text-blue-600">
                        {uploadProgress > 0 ? `Subiendo... ${uploadProgress}%` : 'Subiendo...'}
                      </span>
                    </div>
                  )}
                </div>

                {selectedFiles.length > 0 && (
                  <div className="mt-3">
                    <p className="text-xs font-medium text-gray-500 mb-1">Archivos seleccionados:</p>
                    <div className="flex flex-wrap gap-2">
                      {selectedFiles.map((file, index) => (
                        <div key={index} className="flex items-center gap-2 bg-gray-50 px-3 py-1.5 rounded-lg border border-gray-200">
                          {file.type === 'application/pdf' ? <File size={14} className="text-red-500" /> : <Image size={14} className="text-blue-500" />}
                          <span className="text-xs text-gray-600 truncate max-w-25">{file.name}</span>
                          <button type="button" onClick={() => removeSelectedFile(index)} className="text-red-500 hover:text-red-700">
                            <X size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {imagenes.length > 0 && (
                  <div className="mt-3">
                    <p className="text-xs font-medium text-gray-500 mb-1">Imágenes subidas:</p>
                    <div className="flex flex-wrap gap-2">
                      {imagenes.map((url, index) => (
                        <div key={index} className="relative group">
                          <img src={url} alt={`Compra ${index + 1}`} className="w-16 h-16 object-cover rounded-lg border border-gray-200" />
                          <button type="button" onClick={() => removeImagen(url)} className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                            <X size={12} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {pdfUrl && (
                  <div className="mt-2">
                    <div className="flex items-center gap-2 bg-blue-50 px-3 py-2 rounded-lg border border-blue-200">
                      <File size={16} className="text-red-500" />
                      <a href={pdfUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline truncate max-w-xs">
                        {pdfUrl.split('/').pop() || 'Ver PDF'}
                      </a>
                      <button type="button" onClick={removePdf} className="text-red-500 hover:text-red-700">
                        <X size={14} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </Card>
          </div>

          {/* Columna derecha */}
          <div className="space-y-6">
            <Card className="p-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">Costos</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Costo de Envío</label>
                  <Input type="number" step="0.01" {...register('costoEnvio', { valueAsNumber: true })} placeholder="0.00" error={errors.costoEnvio?.message} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Descuento (%)</label>
                  <Input
                    type="number"
                    step="1"
                    min="0"
                    max="100"
                    {...register('descuento', { valueAsNumber: true })}
                    placeholder="0"
                    error={errors.descuento?.message}
                    onChange={(e) => {
                      const val = parseFloat(e.target.value) || 0;
                      const clamped = Math.min(Math.max(val, 0), 100);
                      setValue('descuento', clamped);
                    }}
                  />
                  <p className="text-xs text-gray-400 mt-1">Porcentaje de descuento sobre el subtotal</p>
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">Resumen</h2>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Subtotal</span>
                  <span className="font-medium">{formatCurrency(subtotal)}</span>
                </div>
                {watchCostoEnvio > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Costo de Envío</span>
                    <span className="font-medium">{formatCurrency(watchCostoEnvio)}</span>
                  </div>
                )}
                {watchDescuento > 0 && (
                  <div className="flex justify-between text-sm text-green-600">
                    <span>Descuento ({watchDescuento}%)</span>
                    <span>-{formatCurrency(subtotal * (watchDescuento / 100))}</span>
                  </div>
                )}
                <div className="border-t pt-2 mt-2">
                  <div className="flex justify-between font-bold text-lg">
                    <span>Total</span>
                    <span className="text-blue-600">{formatCurrency(total)}</span>
                  </div>
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">Observaciones</h2>
              <textarea {...register('observaciones')} rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Observaciones adicionales..." />
            </Card>

            <div className="flex gap-3">
              <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700 text-white" disabled={isSubmitting || uploading}>
                <Save size={18} />
                {uploading ? 'Subiendo archivos...' : isSubmitting ? 'Guardando...' : isEditing ? 'Actualizar' : 'Crear'} Compra
              </Button>
              <Button type="button" variant="outline" onClick={() => navigate('/compras')} className="flex-1">
                Cancelar
              </Button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}